"use client";

import React, { useRef, ReactNode } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
} from "framer-motion";

interface ScrollLockCardProps {
  children: ReactNode;
  index: number;
  totalCards: number;
  scrollProgress: MotionValue<number>;
  className?: string;
  yOffset?: number; // Optional vertical offset for floating effect
}

/**
 * A single card that animates from right to left based on scroll progress.
 * Use this inside HorizontalScrollCards container.
 */
export function ScrollLockCard({
  children,
  index,
  totalCards,
  scrollProgress,
  className = "",
  yOffset = 0,
}: ScrollLockCardProps) {
  // Calculate animation segment based on card index and total cards
  // Distributes the animation evenly across the scroll distance
  const segmentSize = 0.8 / totalCards; // Use 80% of scroll for animations, 20% for settling
  const segmentStart = 0.1 + index * segmentSize;
  const segmentEnd = segmentStart + segmentSize * 0.6; // Card takes 60% of its segment to animate

  // Card slides from right (off-screen) to its position
  const x = useTransform(
    scrollProgress,
    [segmentStart, segmentEnd],
    ["100vw", "0vw"]
  );

  const opacity = useTransform(
    scrollProgress,
    [segmentStart, segmentStart + 0.02, segmentEnd],
    [0, 1, 1]
  );

  return (
    <motion.div
      style={{
        x,
        opacity,
        y: yOffset,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

interface HorizontalScrollCardsProps {
  children: (scrollProgress: MotionValue<number>) => ReactNode;
  scrollHeight?: string; // Height of scroll container (default: "300vh")
  className?: string; // Additional classes for the cards grid container
}

export function HorizontalScrollCards({
  children,
  scrollHeight = "300vh",
  className = "",
}: HorizontalScrollCardsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Track scroll progress through the container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Add spring physics for smooth animation
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <div
      ref={containerRef}
      style={{ height: scrollHeight }}
      className="relative"
    >
      {/* Sticky area - pins while scrolling through container */}
      <div
        className={`sticky top-0 flex h-screen items-center justify-center overflow-hidden ${className}`}
      >
        <div className="mx-auto w-full max-w-[1200px] px-5 md:px-20">
          {children(smoothProgress)}
        </div>
      </div>
    </div>
  );
}

export function useHorizontalScrollAnimation() {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  /**
   * Custom Hook: Get animation values (x, opacity) for a specific card.
   * @param index - The index of the card (0-based)
   * @param totalCards - Total number of cards
   * @returns Object with x and opacity MotionValues
   */
  const useCardAnimationValues = (index: number, totalCards: number) => {
    const segmentSize = 0.8 / totalCards;
    const segmentStart = 0.1 + index * segmentSize;
    const segmentEnd = segmentStart + segmentSize * 0.6;

    const x = useTransform(
      scrollYProgress,
      [segmentStart, segmentEnd],
      ["100vw", "0vw"]
    );

    const opacity = useTransform(
      scrollYProgress,
      [segmentStart, segmentStart + 0.02, segmentEnd],
      [0, 1, 1]
    );

    return { x, opacity };
  };

  return {
    containerRef,
    scrollProgress: scrollYProgress,
    useCardAnimationValues,
  };
}
